import React, { useEffect, useRef, useState } from "react"
import { Animated, ScrollView, StyleSheet, View } from "react-native"
import IconButtonM from "@common/components/button/IconButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import EventCard from "./EventCard"
import { countActiveEvents } from "./eventsCatalog"

const TutorEventsDrawer = ({
    open,
    onClose,
    events,
    onPrimaryAction,
    onDismiss,
    width,
    maxHeight = 380,
    bottom = 70,
    right = 16,
}) => {
    const drawerAnim = useRef(new Animated.Value(0)).current

    useEffect(() => {
        Animated.timing(drawerAnim, {
            toValue: open ? 1 : 0,
            duration: 220,
            useNativeDriver: false,
        }).start()
    }, [open, drawerAnim])

    // Single 1Hz ticker shared by every card (only while open) so the elapsed
    // counters update off one timer instead of one per card.
    const [now, setNow] = useState(Date.now())
    useEffect(() => {
        if (!open) return undefined
        setNow(Date.now())
        const t = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(t)
    }, [open])

    const translateY = drawerAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [320, 0],
    })

    const activeCount = countActiveEvents(events)

    return (
        <Animated.View
            pointerEvents={open ? "auto" : "none"}
            style={[
                styles.drawer,
                {
                    width,
                    maxHeight,
                    bottom,
                    right,
                    opacity: drawerAnim,
                    transform: [{ translateY }],
                },
            ]}
        >
            <View style={styles.drawerHeader}>
                <View style={styles.headerLeft}>
                    <TextM
                        color={theme.colors.blackLight}
                        fontSize={14}
                        fontWeight="ExtraBold"
                    >
                        Événements
                    </TextM>
                    {activeCount > 0 && (
                        <View style={styles.countPill}>
                            <TextM
                                color={theme.colors.white}
                                fontSize={11}
                                fontWeight="ExtraBold"
                            >
                                {activeCount}
                            </TextM>
                        </View>
                    )}
                </View>
                <IconButtonM
                    icon="times"
                    size={16}
                    iconColor={theme.colors.greyAlt}
                    onPress={onClose}
                />
            </View>

            {events.length === 0 ? (
                <View style={styles.empty}>
                    <TextM fontSize={28} center>
                        💜
                    </TextM>
                    <TextM
                        color={theme.colors.primary}
                        fontSize={13}
                        fontWeight="ExtraBold"
                        center
                        style={{ marginTop: 6 }}
                    >
                        Tout va bien
                    </TextM>
                    <TextM
                        color={theme.colors.greyAlt}
                        fontSize={12}
                        center
                        wrap
                        style={{ marginTop: 4 }}
                    >
                        Tu peux te concentrer sur ta session.
                    </TextM>
                </View>
            ) : (
                <ScrollView style={{ maxHeight: maxHeight - 50 }}>
                    {events.map((e) => (
                        <EventCard
                            key={e.hash}
                            event={e}
                            now={now}
                            onPrimaryAction={onPrimaryAction}
                            onDismiss={
                                e.cta?.kind === "passive" || e.isResolved
                                    ? null
                                    : onDismiss
                            }
                        />
                    ))}
                </ScrollView>
            )}
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    drawer: {
        position: "absolute",
        zIndex: 40,
        backgroundColor: theme.colors.white,
        borderRadius: 15,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: -8 },
        shadowOpacity: 0.18,
        shadowRadius: 22,
        elevation: 8,
        overflow: "hidden",
    },
    drawerHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lighter,
    },
    headerLeft: {
        flexDirection: "row",
        alignItems: "center",
    },
    countPill: {
        backgroundColor: theme.colors.error,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 100,
        marginLeft: 8,
    },
    empty: {
        paddingVertical: 36,
        paddingHorizontal: 20,
        alignItems: "center",
    },
})

export default TutorEventsDrawer
